/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useMemo } from 'react';

import { css, jsx } from '@compiled/react';

import { AvatarContent } from '@atlaskit/avatar';
import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';

/*
 * The original AkAvatarGroup cannot be used inside an AkFlag, because the More button (e.g. `+2`)
 * opens a popup whose z-index is too low, and gets displayed below the flag.
 */
export const FlagCompatibleAvatarGroup = ({
	data,
	...props
}: Omit<AvatarGroupProps, 'onMoreClick'>): JSX.Element => {
	const { data: customData, overrides: customOverrides } = useMemo(
		() => getCustomAvatarGroupProps({ data }),
		[data],
	);

	return (
		<AvatarGroup
			maxCount={999}
			data={customData}
			overrides={customOverrides}
			boundariesElement="scrollParent"
			tooltipPosition="top"
			size="small"
			onMoreClick={() => {}} // Prevents the More popup. XXX: The popup has the wrong zIndex and displays under the flag
			{...props}
		/>
	);
};

const MAX_AVATARS_FEATURED = 2;
const MAX_OVERFLOW_USER_COUNT_DISPLAYED = 99;

const OVERFLOW_USER_MARKER = '$$$overflow-user-marker';

export const getCustomAvatarGroupProps = ({
	data: users,
}: Pick<AvatarGroupProps, 'data'>): Pick<AvatarGroupProps, 'data' | 'overrides'> => {
	const overflows = users.length > MAX_AVATARS_FEATURED;
	const lastFeaturedUser = MAX_AVATARS_FEATURED - 1;
	const featuredUsers = users.slice(0, lastFeaturedUser);
	const overflowUsers = users.slice(lastFeaturedUser);
	const overflowMarkerDummyUser = getOverflowMarkerDummyUser(overflowUsers);
	const processedUsers = overflows ? [...featuredUsers, overflowMarkerDummyUser] : users;
	const overrides: AvatarGroupProps['overrides'] = {
		Avatar: {
			render: (AvatarComponent, avatarProps) => {
				if (
					avatarProps.name === overflowMarkerDummyUser.name &&
					avatarProps.key === overflowMarkerDummyUser.key
				) {
					return renderAvatarWithOverflowCount(AvatarComponent, avatarProps, overflowUsers.length);
				}
				return <AvatarComponent {...avatarProps} />;
			},
		},
	};
	return { overrides, data: processedUsers };
};

export const getOverflowMarkerDummyUser = (
	overflowUsers: AvatarGroupProps['data'],
): {
	key: string;
	name: string;
} => ({
	name: overflowUsers.map((u) => u.name).join(', '),
	key: OVERFLOW_USER_MARKER,
});

type AvatarRender = NonNullable<
	NonNullable<NonNullable<AvatarGroupProps['overrides']>['Avatar']>['render']
>;
type AvatarRenderArgs = Parameters<AvatarRender>;

const renderAvatarWithOverflowCount = (
	AvatarComponent: AvatarRenderArgs[0],
	avatarProps: AvatarRenderArgs[1],
	overflowCount: number,
): ReturnType<AvatarRender> => (
	<AvatarComponent
		name={avatarProps.name}
		appearance={avatarProps.appearance}
		size={avatarProps.size}
		borderColor={avatarProps.borderColor}
		stackIndex={0}
	>
		<AvatarContent>
			<span css={avatarOverflowCountContainer}>
				{`+${Math.min(overflowCount, MAX_OVERFLOW_USER_COUNT_DISPLAYED)}`}
			</span>
		</AvatarContent>
	</AvatarComponent>
);

const avatarOverflowCountContainer = css({
	width: '100%',
	height: '100%',
	display: 'grid',
	placeItems: 'center',
	backgroundColor: token('color.background.neutral'),
	color: token('color.text'),
	textAlign: 'center',
	font: token('font.body.small'),
	fontWeight: token('font.weight.medium'),
});
